import Phaser from 'phaser';

let installed = false;
let cachedResolution: number | null = null;

/** Resolusi render teks Phaser — minimum 2× agar tajam di layar HD/retina */
export function getTextResolution(): number {
  if (cachedResolution !== null) return cachedResolution;
  if (typeof window === 'undefined') {
    cachedResolution = 2;
    return cachedResolution;
  }
  const dpr = window.devicePixelRatio || 1;
  cachedResolution = Math.min(Math.max(dpr, 2), 3);
  return cachedResolution;
}

/** Reset cache saat DPR berubah (pindah monitor / zoom browser) */
export function invalidateTextResolutionCache(): void {
  cachedResolution = null;
}

/** Terapkan resolusi HD pada objek teks */
export function enhanceText(text: Phaser.GameObjects.Text): Phaser.GameObjects.Text {
  text.setResolution(getTextResolution());
  return text;
}

function patchTextPrototype(): void {
  type TextProto = Phaser.GameObjects.Text & { __crispPatched?: boolean };
  const proto = Phaser.GameObjects.Text.prototype as TextProto;
  if (proto.__crispPatched) return;
  proto.__crispPatched = true;

  const reapply = function reapply(this: Phaser.GameObjects.Text) {
    enhanceText(this);
    return this;
  };

  const originalSetStyle = proto.setStyle;
  proto.setStyle = function setStyle(
    style: Phaser.Types.GameObjects.Text.TextStyle,
    updateText?: boolean,
    setDefaults?: boolean,
  ) {
    (originalSetStyle as (
      s: Phaser.Types.GameObjects.Text.TextStyle,
      u?: boolean,
      d?: boolean,
    ) => Phaser.GameObjects.Text).call(this, style, updateText, setDefaults);
    return reapply.call(this);
  };

  const originalSetText = proto.setText;
  proto.setText = function setText(value) {
    originalSetText.call(this, value);
    return reapply.call(this);
  };

  const originalSetColor = proto.setColor;
  proto.setColor = function setColor(color) {
    originalSetColor.call(this, color);
    return reapply.call(this);
  };

  const originalSetFontSize = proto.setFontSize;
  proto.setFontSize = function setFontSize(size) {
    originalSetFontSize.call(this, size);
    return reapply.call(this);
  };
}

/** Patch factory Phaser agar semua `add.text()` otomatis tajam */
export function installCrispText(): void {
  if (installed) return;
  installed = true;

  patchTextPrototype();

  const factory = Phaser.GameObjects.GameObjectFactory.prototype;
  const original = factory.text;

  factory.text = function textWithResolution(
    this: Phaser.GameObjects.GameObjectFactory,
    x: number,
    y: number,
    text: string | string[],
    style?: Phaser.Types.GameObjects.Text.TextStyle,
  ) {
    const obj = original.call(this, x, y, text, style);
    enhanceText(obj);
    return obj;
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('resize', invalidateTextResolutionCache, { passive: true });
  }
}
